// src/components/Login/Login.js

import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { auth, googleProvider } from "../../firebaseConfig";
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  sendPasswordResetEmail 
} from "firebase/auth";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import "react-toastify/ReactToastify.css";
import "./Login.css";

const PROJECT_KEY = "ijrr_user";

export default function Login({ onLoginSuccess }) {
  const { redirectAuthenticatedUser } = useAuthRedirect();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // State to toggle between Login form and Reset Password form
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const redirectUser = (userData) => {
    localStorage.setItem(PROJECT_KEY, JSON.stringify(userData));
    console.log("Login successful", userData);

    toast.success("Welcome back to the ship!", { autoClose: 1000 });

    setTimeout(() => {
      // FIX: Pass 'login' action to force redirection to /ijrr-gear
      redirectAuthenticatedUser(userData, 'login');
    }, 1500);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers/${user.uid}`);
      let userData = {
        firebase_uid: user.uid,
        email: user.email,
        sign_up_method: "email_pass",
        status: "active",
        created_at_firebase: new Date(user.metadata.creationTime).toISOString(),
        last_login_at_firebase: new Date(user.metadata.lastSignInTime).toISOString(),
      };

      if (response.ok) {
        const data = await response.json();
        userData = {
          ...userData,
          first_name: data.customer.first_name,
          last_name: data.customer.last_name,
          mobile: data.customer.mobile,
          profile_completed_at: data.customer.profile_completed_at,
        };
      } else {
        console.warn("User not found in database, might be new.");
      }

      redirectUser(userData);
    } catch (error) {
      console.error("Login error:", error.code, error.message);
      
      // Robust Error Handling including Email Enumeration Protection codes
      switch (error.code) {
        case "auth/invalid-email":
          toast.error("Invalid email address format.");
          break;
        case "auth/user-disabled":
          toast.error("This account has been disabled. Please contact support.");
          break;
        
        // Firebase generic errors (covers both wrong password and user not found)
        case "auth/invalid-credential": 
        case "auth/invalid-login-credentials":
          toast.error("Invalid email or password.");
          break;

        // Legacy or specific configuration errors
        case "auth/user-not-found":
        case "auth/wrong-password":
          toast.error("Invalid email or password.");
          break;

        case "auth/too-many-requests":
          toast.warning("Too many failed attempts. Please try again later.");
          break;
        case "auth/network-request-failed":
          toast.error("Network error. Please check your connection.");
          break;
        default:
          toast.error("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const names = user.displayName ? user.displayName.split(" ") : ["", ""];
      let userData = {
        firebase_uid: user.uid,
        first_name: names[0],
        last_name: names.slice(1).join(" "),
        email: user.email,
        avatar: user.photoURL,
        sign_up_method: "google",
        status: "active",
        created_at_firebase: new Date(user.metadata.creationTime).toISOString(),
        last_login_at_firebase: new Date(user.metadata.lastSignInTime).toISOString(),
      };

      let response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers/${user.uid}`);

      if (response.ok) {
        const data = await response.json();
        userData = {
          ...userData,
          first_name: data.customer.first_name,
          last_name: data.customer.last_name,
          mobile: data.customer.mobile,
          profile_completed_at: data.customer.profile_completed_at,
        };
        console.log("User found in API:", userData);
      } else {
        console.warn("User not found in API, registering...");

        const registerResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });

        if (!registerResponse.ok) {
          const errorData = await registerResponse.json();
          console.error("Error registering user:", errorData);
          toast.error("Failed to save user data. Please try again.");
          setIsLoading(false);
          return;
        }

        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/customers/${user.uid}`);
        if (response.ok) {
          const data = await response.json();
          userData = {
            ...userData,
            first_name: data.customer.first_name,
            last_name: data.customer.last_name,
            mobile: data.customer.mobile,
            profile_completed_at: data.customer.profile_completed_at,
          };
        }
      }

      redirectUser(userData);
    } catch (error) {
      console.error("Google sign-in error:", error);
      toast.error("Google sign-in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address first.");
      return;
    }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset email sent! Check your inbox.");
      setIsResettingPassword(false); 
    } catch (error) {
      console.error("Password reset error:", error);
      if (error.code === "auth/user-not-found") {
        toast.error("No account found with this email.");
      } else {
        toast.error("Failed to send reset email. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="login-page"> 
      <ToastContainer autoClose="1500" />
      <div className="login-container">
        <div className="codex-authmain">
          
          <div className="codex-authbox">
            <div className="group-form text-center">
              <h3>{isResettingPassword ? "Reset Password" : "Login to Your Account"}</h3>
              {!isResettingPassword && (
                <h6>
                  Don't have an account?{" "}
                  <a className="text-primary text-decoration-underline" href="/register">
                    Sign up here
                  </a>
                </h6>
              )}
            </div>

            {!isResettingPassword ? (
              // --- LOGIN FORM ---
              <form onSubmit={handleLogin}>
                <div className="group-form">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    type="email"
                    placeholder="mail@jollyroger.us.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="group-form">
                  <label className="form-label">Password</label>
                  <input
                    className="form-control"
                    type="password"
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  {/* FORGOT PASSWORD LINK - ALWAYS VISIBLE */}
                  <div className="text-end mt-1">
                    <button 
                      type="button" 
                      className="btn-link text-primary" 
                      onClick={() => setIsResettingPassword(true)}
                      style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.9rem' }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn d-block btn-primary-login" disabled={isLoading}>
                  {isLoading ? "Logging In..." : "Log In"}
                </button>

                <button type="button" className="auth-google-btn" onClick={handleGoogleSignIn} disabled={isLoading}>
                  Sign in with Google
                </button>
              </form>
            ) : (
              // --- RESET PASSWORD FORM ---
              <form onSubmit={handlePasswordReset}>
                 <p className="mb-3 text-muted text-center">
                   Enter your email address and we'll send you a link to reset your password.
                 </p>
                 <div className="group-form">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    type="email"
                    placeholder="mail@jollyroger.us.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <button type="submit" className="btn d-block btn-primary-login" disabled={isLoading}>
                  {isLoading ? "Sending..." : "Send Reset Link"}
                </button>

                <div className="text-center mt-3">
                  <button 
                    type="button" 
                    className="btn-link text-secondary"
                    onClick={() => setIsResettingPassword(false)}
                    style={{ background: 'none', border: 'none', textDecoration: 'underline' }}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}